import { useEffect, useRef } from "react";
import {
    Activity,
    MousePointer2,
    Paintbrush,
    Palette,
    Ruler,
    Share2,
    Spline,
    Upload,
    Users,
} from "lucide-react";
import { CheckboxField } from "../reusable/CheckboxField";
import { CollapsibleToolGroup } from "../reusable/CollapsibleToolGroup";
import {
    usePathAgents,
    type AgentBrushMode,
    type BrushStampSpacingMode,
    type ChildTrailCapMode,
    type DirectionLockMode,
} from "./PathAgentsContext";
import { DebouncedInput } from "../reusable/DebouncedInput";
import { PresetSelector } from "../reusable/PresetSelector";
import { Switch } from "../reusable/Switch";
import { ToolDescriptionPopover } from "../reusable/ToolDescriptionPopover";
import { ColorPaletteEditor } from "../reusable/ColorPaletteEditor";
import { colorsToPaletteText, parsePaletteTextToColors } from "../../utils/pathAgents";
import { isValidSvg } from "../../utils/svgUtils";
import { SendSvgToToolMenu } from "../reusable/SendSvgToToolMenu";

export function PathAgentsControls() {
    const {
        kickIntervalSec,
        kickIntervalRandomnessSec,
        velocityKickGradual,
        setVelocityKickGradual,
        velocityKickBlendFrames,
        setVelocityKickBlendFrames,
        forceMagnitude,
        baseSpeed,
        maxSpeed,
        directionLock,
        setDirectionLock,
        maxTrailLength,
        maxTrailLengthRandomness,
        spawnChildIntervalSec,
        spawnChildIntervalRandomnessSec,
        childTrailCapMode,
        childTrailCapFixed,
        childTrailCapRandomness,
        gravity,
        strokeColor,
        strokePaletteText,
        setStrokePaletteText,
        randomStrokeColorPerAgent,
        setRandomStrokeColorPerAgent,
        strokeWidth,
        minStrokeWidth,
        initialStrokeWidth,
        strokeWidthStep,
        strokeWidthKickFrames,
        strokeWidthKickGradual,
        strokeWidthBlendFrames,
        backgroundColor,
        exportBackground,
        setExportBackground,
        smoothPaths,
        setSmoothPaths,
        mergeTrailToSinglePath,
        setMergeTrailToSinglePath,
        unionGroupOnComplete,
        setUnionGroupOnComplete,
        filledVariableWidthStroke,
        setFilledVariableWidthStroke,
        trailTaperStartLength,
        setTrailTaperStartLength,
        trailTaperEndLength,
        setTrailTaperEndLength,
        dashedStroke,
        setDashedStroke,
        strokeDashLength,
        setStrokeDashLength,
        strokeDashGap,
        setStrokeDashGap,
        spiralTrail,
        setSpiralTrail,
        spiralAmplitude,
        setSpiralAmplitude,
        spiralWavelength,
        setSpiralWavelength,
        spiralGrowthPer1k,
        setSpiralGrowthPer1k,
        completedPaths,
        activeAgents,
        setKickIntervalSec,
        setKickIntervalRandomnessSec,
        setForceMagnitude,
        setBaseSpeed,
        setMaxSpeed,
        setMaxTrailLength,
        setMaxTrailLengthRandomness,
        stopAtMargin,
        setStopAtMargin,
        setSpawnChildIntervalSec,
        setSpawnChildIntervalRandomnessSec,
        setChildTrailCapMode,
        setChildTrailCapFixed,
        setChildTrailCapRandomness,
        setGravity,
        setStrokeColor,
        setStrokeWidth,
        setMinStrokeWidth,
        setInitialStrokeWidth,
        setStrokeWidthStep,
        setStrokeWidthKickFrames,
        setStrokeWidthKickGradual,
        setStrokeWidthBlendFrames,
        setBackgroundColor,
        clearPaths,
        randomizeParameters,
        resetParameters,
        groupSize,
        setGroupSize,
        groupRadius,
        setGroupRadius,
        strokeLayoutCount,
        setStrokeLayoutCount,
        strokeLayoutSpacing,
        setStrokeLayoutSpacing,
        groupCohesionStrength,
        setGroupCohesionStrength,
        groupVariedBrushes,
        setGroupVariedBrushes,
        agentBrushMode,
        setAgentBrushMode,
        brushStampIntervalFrames,
        setBrushStampIntervalFrames,
        brushPolygonSides,
        setBrushPolygonSides,
        brushStampSizeScale,
        setBrushStampSizeScale,
        brushStampSpacingMode,
        setBrushStampSpacingMode,
        brushStampDistancePx,
        setBrushStampDistancePx,
        brushTextContent,
        setBrushTextContent,
        brushTextStrokeUnderlayColor,
        setBrushTextStrokeUnderlayColor,
        brushTextStrokeUnderlayScale,
        setBrushTextStrokeUnderlayScale,
        brushTextRandomStartOffset,
        setBrushTextRandomStartOffset,
        exportGroupTextByAgent,
        setExportGroupTextByAgent,
        applyPreset,
        getPresetValues,
        inputMode,
        setInputMode,
        canExport,
        buildExportSvgString,
        spawnGuideSvg,
        setSpawnGuideSvg,
        spawnGuideSamplePx,
        setSpawnGuideSamplePx,
        spawnGuideMaxAgents,
        setSpawnGuideMaxAgents,
        spawnGuideAlignTangent,
        setSpawnGuideAlignTangent,
        spawnGuideError,
        setSpawnGuideError,
        clearSpawnGuide,
        spawnAgentsFromImportedGuide,
    } = usePathAgents();

    const strokeTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
    const bgTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
    const spawnGuideFileRef = useRef<HTMLInputElement | null>(null);

    useEffect(() => {
        return () => {
            if (strokeTimeout.current) clearTimeout(strokeTimeout.current);
            if (bgTimeout.current) clearTimeout(bgTimeout.current);
        };
    }, []);

    const handleStrokePicker = (value: string) => {
        if (strokeTimeout.current) clearTimeout(strokeTimeout.current);
        strokeTimeout.current = setTimeout(() => setStrokeColor(value), 150);
    };

    const handleBgPicker = (value: string) => {
        if (bgTimeout.current) clearTimeout(bgTimeout.current);
        bgTimeout.current = setTimeout(() => setBackgroundColor(value), 150);
    };

    const handleFloat =
        (setter: (v: number) => void, min = 0, max = Infinity) =>
        (v: string | number) => {
            const n = typeof v === "number" ? v : Number(v);
            if (!Number.isFinite(n) || n < min || n > max) return;
            setter(n);
        };

    const handleInt =
        (setter: (v: number) => void, min = 0, max = Infinity) =>
        (v: string | number) => {
            const n = typeof v === "number" ? v : Number(v);
            if (!Number.isFinite(n) || n < min || n > max) return;
            setter(Math.round(n));
        };

    const handleSpawnGuideFile = (
        event: React.ChangeEvent<HTMLInputElement>,
    ) => {
        const file = event.target.files?.[0];
        event.target.value = "";
        if (!file) return;
        setSpawnGuideError(null);
        if (
            !file.type.includes("svg") &&
            !file.name.toLowerCase().endsWith(".svg")
        ) {
            setSpawnGuideError("Please choose an SVG file.");
            return;
        }
        const reader = new FileReader();
        reader.onload = () => {
            const text =
                typeof reader.result === "string" ? reader.result : "";
            if (!text) {
                setSpawnGuideError("Unable to read SVG file.");
                return;
            }
            if (!isValidSvg(text)) {
                setSpawnGuideError(
                    "Selected file does not appear to be valid SVG.",
                );
                return;
            }
            setSpawnGuideSvg({ name: file.name, content: text });
        };
        reader.onerror = () => {
            setSpawnGuideError("Error reading file.");
        };
        reader.readAsText(file);
    };

    return (
        <div className="panel-controls blob-field-controls">
            <div className="tool-header">
                <div className="tool-header-title-row">
                    <h2>Path Agents</h2>
                    <ToolDescriptionPopover dialogLabel="Path Agents tool description">
                        <p>
                            Click the canvas to spawn agent(s). With group size
                            greater than 1, agents appear in a disk around the
                            click; one is the leader (gold ring) and the rest
                            pull toward it. Enable varied brushes so each member
                            can use stroke, circles, polygon, text, or text with
                            stroke underlay independently.
                            Solo agents have no ring. When an agent hits the
                            edge its trail stays.
                        </p>
                    </ToolDescriptionPopover>
                </div>
                <div className="tool-header-actions">
                    <button
                        type="button"
                        className="generic-button"
                        onClick={resetParameters}
                    >
                        Reset parameters
                    </button>
                    <button
                        type="button"
                        className="generic-button"
                        onClick={randomizeParameters}
                    >
                        Randomize parameters
                    </button>
                    <button
                        type="button"
                        className="generic-button"
                        onClick={clearPaths}
                        disabled={
                            completedPaths.length === 0 &&
                            activeAgents.length === 0
                        }
                    >
                        Clear all paths
                    </button>
                </div>
            </div>

            <div className="path-tool-inputs">
                <PresetSelector
                    toolId="path-agents"
                    onApply={applyPreset}
                    onExport={getPresetValues}
                />

                <CollapsibleToolGroup
                    title="Artboard input"
                    icon={MousePointer2}
                >
                    <div className="field-row field-row--switch">
                        <Switch
                            checked={inputMode === "dragPaint"}
                            onChange={(on) =>
                                setInputMode(on ? "dragPaint" : "clickSpawn")
                            }
                            label="Drag to paint (puppet group)"
                            title="Off: pointer up spawns; short drag = point, longer drag sets direction. On: pointer down spawns; hold to pull the agent toward the cursor (stroke trail or stamp brushes)."
                        />
                    </div>
                    <p
                        style={{
                            fontSize: "0.85em",
                            opacity: 0.75,
                            margin: "0.35rem 0 0",
                        }}
                    >
                        {inputMode === "clickSpawn"
                            ? "Click / drag direction to spawn."
                            : "Paint by holding and moving; release to let agents run freely."}
                    </p>
                </CollapsibleToolGroup>

                <CollapsibleToolGroup title="Spawn from SVG" icon={Upload}>
                    <input
                        ref={spawnGuideFileRef}
                        type="file"
                        accept=".svg,image/svg+xml"
                        style={{ display: "none" }}
                        onChange={handleSpawnGuideFile}
                    />
                    <p
                        style={{
                            fontSize: "0.85em",
                            opacity: 0.8,
                            margin: "0 0 0.5rem",
                        }}
                    >
                        Import path geometry (paths, polylines, polygons, lines,
                        rects). Use{" "}
                        <strong>Spawn agents from guide</strong> to place agents
                        at sampled positions; the guide viewBox is centered on the
                        artboard. Other tools can send SVG here via{" "}
                        <em>Send to tool</em> (path or source slot). Agents per
                        click and parallel stroke layout still apply per point.
                    </p>
                    <div className="field-row">
                        <button
                            type="button"
                            className="generic-button"
                            onClick={() => spawnGuideFileRef.current?.click()}
                        >
                            Choose guide SVG...
                        </button>
                        {spawnGuideSvg && (
                            <>
                                <span
                                    style={{
                                        fontSize: "0.85em",
                                        opacity: 0.85,
                                        flex: 1,
                                        minWidth: 0,
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                    }}
                                    title={spawnGuideSvg.name}
                                >
                                    {spawnGuideSvg.name}
                                </span>
                                <button
                                    type="button"
                                    className="generic-button"
                                    onClick={clearSpawnGuide}
                                >
                                    Clear guide
                                </button>
                            </>
                        )}
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Sample spacing (px)
                            <DebouncedInput
                                type="number"
                                min={2}
                                max={200}
                                step={1}
                                value={spawnGuideSamplePx}
                                onChange={handleInt(
                                    setSpawnGuideSamplePx,
                                    2,
                                    200,
                                )}
                            />
                        </label>
                        <label className="field-label">
                            Max agents
                            <DebouncedInput
                                type="number"
                                min={1}
                                max={5000}
                                step={1}
                                value={spawnGuideMaxAgents}
                                onChange={handleInt(
                                    setSpawnGuideMaxAgents,
                                    1,
                                    5000,
                                )}
                            />
                        </label>
                    </div>
                    <div className="field-row">
                        <CheckboxField
                            checked={spawnGuideAlignTangent}
                            onChange={setSpawnGuideAlignTangent}
                            label="Align initial velocity to path tangent"
                        />
                    </div>
                    <div className="field-row">
                        <button
                            type="button"
                            className="generic-button"
                            onClick={() => spawnAgentsFromImportedGuide()}
                            disabled={!spawnGuideSvg}
                        >
                            Spawn agents from guide
                        </button>
                    </div>
                    {spawnGuideError && (
                        <p className="upload-error">{spawnGuideError}</p>
                    )}
                </CollapsibleToolGroup>

                <CollapsibleToolGroup title="Stroke trail" icon={Spline}>
                    <div className="field-row">
                        <CheckboxField
                            checked={smoothPaths}
                            onChange={setSmoothPaths}
                            label="Smooth paths (curves)"
                            disabled={agentBrushMode !== "stroke"}
                        />
                    </div>
                    <div className="field-row">
                        <CheckboxField
                            checked={mergeTrailToSinglePath}
                            onChange={setMergeTrailToSinglePath}
                            label="Single path for varying width"
                            title="When stroke width changes along the trail, draw one path with average width instead of one line per segment."
                            disabled={agentBrushMode !== "stroke"}
                        />
                    </div>
                    <div className="field-row">
                        <CheckboxField
                            checked={filledVariableWidthStroke}
                            onChange={setFilledVariableWidthStroke}
                            label="Variable-width fill (outline)"
                            title="Renders stroke as a filled polygon outline from per-vertex widths (miter joins). Uses polyline geometry; smooth curves and dashed stroke do not apply to the fill."
                            disabled={
                                agentBrushMode !== "stroke" &&
                                agentBrushMode !== "textWithStroke"
                            }
                        />
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Taper start (px)
                            <DebouncedInput
                                type="number"
                                min={0}
                                max={2400}
                                step={1}
                                value={trailTaperStartLength}
                                onChange={handleFloat(
                                    setTrailTaperStartLength,
                                    0,
                                    2400,
                                )}
                                disabled={agentBrushMode !== "stroke"}
                            />
                        </label>
                        <label className="field-label">
                            Taper end (px)
                            <DebouncedInput
                                type="number"
                                min={0}
                                max={2400}
                                step={1}
                                value={trailTaperEndLength}
                                onChange={handleFloat(
                                    setTrailTaperEndLength,
                                    0,
                                    2400,
                                )}
                                disabled={agentBrushMode !== "stroke"}
                            />
                        </label>
                    </div>
                    <div className="field-row">
                        <CheckboxField
                            checked={dashedStroke}
                            onChange={setDashedStroke}
                            label="Dashed stroke"
                            title='Dashes restart on each line segment when varying width uses many lines; prefer "Single path for varying width" for a continuous dash pattern.'
                            disabled={agentBrushMode !== "stroke"}
                        />
                    </div>
                    {dashedStroke && agentBrushMode === "stroke" && (
                        <>
                            <div className="field-row">
                                <label className="field-label">
                                    Dash length
                                    <DebouncedInput
                                        type="number"
                                        min={0.5}
                                        max={500}
                                        step={0.5}
                                        value={strokeDashLength}
                                        onChange={handleFloat(
                                            setStrokeDashLength,
                                            0.5,
                                            500,
                                        )}
                                    />
                                </label>
                            </div>
                            <div className="field-row">
                                <label className="field-label">
                                    Gap length
                                    <DebouncedInput
                                        type="number"
                                        min={0.5}
                                        max={500}
                                        step={0.5}
                                        value={strokeDashGap}
                                        onChange={handleFloat(
                                            setStrokeDashGap,
                                            0.5,
                                            500,
                                        )}
                                    />
                                </label>
                            </div>
                        </>
                    )}

                    <div className="field-row">
                        <CheckboxField
                            checked={spiralTrail}
                            onChange={setSpiralTrail}
                            label="Spiral trail"
                            title="Offset rotates in the motion plane: sin along perpendicular, cos along tangent (optional widening with length)."
                            disabled={agentBrushMode !== "stroke"}
                        />
                    </div>
                    {spiralTrail && agentBrushMode === "stroke" && (
                        <>
                            <div className="field-row">
                                <label
                                    className="field-label"
                                    title="Lateral offset (viewBox units)"
                                >
                                    Spiral amplitude
                                    <DebouncedInput
                                        type="number"
                                        min={0.5}
                                        max={120}
                                        step={0.5}
                                        value={spiralAmplitude}
                                        onChange={handleFloat(
                                            setSpiralAmplitude,
                                            0.5,
                                            120,
                                        )}
                                    />
                                </label>
                            </div>
                            <div className="field-row">
                                <label
                                    className="field-label"
                                    title="Distance along the path for one full wiggle cycle"
                                >
                                    Wavelength
                                    <DebouncedInput
                                        type="number"
                                        min={8}
                                        max={400}
                                        step={1}
                                        value={spiralWavelength}
                                        onChange={handleFloat(
                                            setSpiralWavelength,
                                            8,
                                            400,
                                        )}
                                    />
                                </label>
                            </div>
                            <div className="field-row">
                                <label
                                    className="field-label"
                                    title="0 = even waves; higher = amplitude grows with path length"
                                >
                                    Growth / 1000
                                    <DebouncedInput
                                        type="number"
                                        min={0}
                                        max={2}
                                        step={0.01}
                                        value={spiralGrowthPer1k}
                                        onChange={handleFloat(
                                            setSpiralGrowthPer1k,
                                            0,
                                            2,
                                        )}
                                    />
                                </label>
                            </div>
                        </>
                    )}
                </CollapsibleToolGroup>

                <CollapsibleToolGroup title="Brush" icon={Paintbrush}>
                    <p
                        style={{
                            fontSize: "0.85em",
                            opacity: 0.8,
                            margin: "0 0 0.5rem",
                        }}
                    >
                        Stroke draws a continuous path. Circles and polygons
                        stamp a shape on each sample (by frame interval or
                        distance along the path). Text stamps one character per
                        sample, aligned with motion; leave brush text empty to
                        assign random text per new agent. Text + stroke underlay
                        uses the same lettering plus a thick stroke trail drawn
                        under the glyphs (underlay color and width scale).
                    </p>
                    <div className="field-row">
                        <label className="field-label">
                            Brush mode
                            <select
                                value={agentBrushMode}
                                onChange={(e) =>
                                    setAgentBrushMode(
                                        e.target.value as AgentBrushMode,
                                    )
                                }
                                aria-label="Brush mode"
                            >
                                <option value="stroke">Stroke (default)</option>
                                <option value="circles">Circles</option>
                                <option value="polygon">Polygon</option>
                                <option value="text">Text</option>
                                <option value="textWithStroke">
                                    Text + stroke underlay
                                </option>
                            </select>
                        </label>
                    </div>
                    {(agentBrushMode === "text" ||
                        agentBrushMode === "textWithStroke") && (
                        <div className="field-row">
                            <label
                                className="field-label"
                                title="Characters are placed along the path in order and repeat. Leave empty to generate random text for each new agent."
                            >
                                Brush text
                                <textarea
                                    value={brushTextContent}
                                    onChange={(e) =>
                                        setBrushTextContent(e.target.value)
                                    }
                                    rows={4}
                                    style={{ width: "100%", resize: "vertical" }}
                                    placeholder="Leave empty for random letters and symbols"
                                    spellCheck={false}
                                />
                            </label>
                        </div>
                    )}
                    {agentBrushMode === "textWithStroke" && (
                        <div className="field-row">
                            <label className="field-label">
                                Underlay color
                                <input
                                    type="color"
                                    value={brushTextStrokeUnderlayColor}
                                    onChange={(e) =>
                                        setBrushTextStrokeUnderlayColor(
                                            e.target.value,
                                        )
                                    }
                                />
                            </label>
                        </div>
                    )}
                    {agentBrushMode === "textWithStroke" && (
                        <div className="field-row">
                            <label className="field-label">
                                Underlay width scale
                                <DebouncedInput
                                    type="number"
                                    min={1}
                                    max={6}
                                    step={0.05}
                                    value={brushTextStrokeUnderlayScale}
                                    onChange={handleFloat(
                                        setBrushTextStrokeUnderlayScale,
                                        1,
                                        6,
                                    )}
                                />
                            </label>
                        </div>
                    )}
                    {(agentBrushMode === "text" ||
                        agentBrushMode === "textWithStroke") && (
                        <div className="field-row">
                            <CheckboxField
                                checked={brushTextRandomStartOffset}
                                onChange={setBrushTextRandomStartOffset}
                                label="Random start position in text"
                                title="Each newly spawned text-brush agent starts at a random character offset in the source string."
                            />
                        </div>
                    )}
                    {(agentBrushMode === "text" ||
                        agentBrushMode === "textWithStroke") && (
                        <div className="field-row">
                            <CheckboxField
                                checked={exportGroupTextByAgent}
                                onChange={setExportGroupTextByAgent}
                                label="Group text by agent in SVG export"
                                title="Wraps each agent's characters in one child group with a data-agent-id attribute inside the spawn group (easier to select or style per agent)."
                            />
                        </div>
                    )}
                    <div className="field-row">
                        <label
                            className="field-label"
                            title="Stroke mode only: parallel paths per group position. Multiplies with agents per click."
                        >
                            Stroke layout (parallel)
                            <DebouncedInput
                                type="number"
                                min={1}
                                max={32}
                                step={1}
                                value={strokeLayoutCount}
                                onChange={handleInt(
                                    setStrokeLayoutCount,
                                    1,
                                    32,
                                )}
                                disabled={agentBrushMode !== "stroke"}
                            />
                        </label>
                    </div>
                    <div className="field-row">
                        <label
                            className="field-label"
                            title="Spacing between parallel stroke starts, perpendicular to the stroke direction (SVG px)."
                        >
                            Layout spacing
                            <DebouncedInput
                                type="number"
                                min={0}
                                max={400}
                                step={1}
                                value={strokeLayoutSpacing}
                                onChange={handleFloat(
                                    setStrokeLayoutSpacing,
                                    0,
                                    400,
                                )}
                                disabled={agentBrushMode !== "stroke"}
                            />
                        </label>
                    </div>
                    {agentBrushMode !== "stroke" && (
                        <>
                            <div className="field-row">
                                <label className="field-label">
                                    Stamp spacing
                                    <select
                                        value={brushStampSpacingMode}
                                        onChange={(e) =>
                                            setBrushStampSpacingMode(
                                                e.target
                                                    .value as BrushStampSpacingMode,
                                            )
                                        }
                                        aria-label="Stamp spacing mode"
                                    >
                                        <option value="frames">
                                            Every N frames
                                        </option>
                                        <option value="distance">
                                            Every N px (along path)
                                        </option>
                                    </select>
                                </label>
                            </div>
                            <div className="field-row">
                                <label
                                    className="field-label"
                                    title={
                                        brushStampSpacingMode === "frames"
                                            ? "Emit one stamp every this many animation frames."
                                            : "Emit stamps along motion so consecutive samples are this many SVG units apart (carries remainder across frames)."
                                    }
                                >
                                    {brushStampSpacingMode === "frames"
                                        ? "Stamp every (frames)"
                                        : "Stamp every (px)"}
                                    <DebouncedInput
                                        type="number"
                                        min={1}
                                        max={
                                            brushStampSpacingMode === "frames"
                                                ? 120
                                                : 800
                                        }
                                        step={
                                            brushStampSpacingMode === "frames"
                                                ? 1
                                                : 1
                                        }
                                        value={
                                            brushStampSpacingMode === "frames"
                                                ? brushStampIntervalFrames
                                                : brushStampDistancePx
                                        }
                                        onChange={
                                            brushStampSpacingMode === "frames"
                                                ? handleInt(
                                                      setBrushStampIntervalFrames,
                                                      1,
                                                      120,
                                                  )
                                                : handleInt(
                                                      setBrushStampDistancePx,
                                                      1,
                                                      800,
                                                  )
                                        }
                                    />
                                </label>
                            </div>
                            <div className="field-row">
                                <label className="field-label">
                                    Polygon sides
                                    <DebouncedInput
                                        type="number"
                                        min={3}
                                        max={24}
                                        step={1}
                                        value={brushPolygonSides}
                                        onChange={handleInt(
                                            setBrushPolygonSides,
                                            3,
                                            24,
                                        )}
                                        disabled={agentBrushMode !== "polygon"}
                                    />
                                </label>
                            </div>
                            <div className="field-row">
                                <label className="field-label">
                                    Stamp size (x stroke width)
                                    <DebouncedInput
                                        type="number"
                                        min={0.25}
                                        max={8}
                                        step={0.05}
                                        value={brushStampSizeScale}
                                        onChange={handleFloat(
                                            setBrushStampSizeScale,
                                            0.25,
                                            8,
                                        )}
                                    />
                                </label>
                            </div>
                        </>
                    )}
                </CollapsibleToolGroup>

                <CollapsibleToolGroup title="Groups" icon={Users}>
                    <div className="field-row">
                        <label className="field-label">
                            Agents per click
                            <DebouncedInput
                                type="number"
                                min={1}
                                max={32}
                                step={1}
                                value={groupSize}
                                onChange={handleInt(setGroupSize, 1, 32)}
                            />
                        </label>
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Group radius
                            <DebouncedInput
                                type="number"
                                min={1}
                                max={999}
                                step={1}
                                value={groupRadius}
                                onChange={handleFloat(setGroupRadius, 1, 999)}
                            />
                        </label>
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Cohesion (follow strength)
                            <DebouncedInput
                                type="number"
                                min={0}
                                max={40}
                                step={0.5}
                                value={groupCohesionStrength}
                                onChange={handleFloat(
                                    setGroupCohesionStrength,
                                    0,
                                    40,
                                )}
                            />
                        </label>
                    </div>
                    <div className="field-row">
                        <CheckboxField
                            checked={groupVariedBrushes}
                            onChange={setGroupVariedBrushes}
                            label="Varied brushes in groups (random per member)"
                        />
                    </div>
                    <div className="field-row">
                        <CheckboxField
                            checked={unionGroupOnComplete}
                            onChange={setUnionGroupOnComplete}
                            label="Unite trails when group completes"
                            title="When a spawn group has no active agents left, merge its completed stroke trails into one boolean-unioned shape in SVG export."
                        />
                    </div>
                </CollapsibleToolGroup>

                <CollapsibleToolGroup title="Motion" icon={Activity}>
                    <div className="field-row">
                        <label className="field-label">
                            Kick interval (seconds)
                            <DebouncedInput
                                type="number"
                                min={0.25}
                                max={60}
                                step={0.25}
                                value={kickIntervalSec}
                                onChange={handleFloat(
                                    setKickIntervalSec,
                                    0.25,
                                    60,
                                )}
                            />
                        </label>
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Interval randomness (+/- seconds)
                            <DebouncedInput
                                type="number"
                                min={0}
                                max={30}
                                step={0.05}
                                value={kickIntervalRandomnessSec}
                                onChange={handleFloat(
                                    setKickIntervalRandomnessSec,
                                    0,
                                    30,
                                )}
                            />
                        </label>
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Force magnitude
                            <DebouncedInput
                                type="number"
                                min={0}
                                max={2000}
                                step={5}
                                value={forceMagnitude}
                                onChange={handleFloat(
                                    setForceMagnitude,
                                    0,
                                    2000,
                                )}
                            />
                        </label>
                    </div>
                    <div className="field-row">
                        <CheckboxField
                            checked={velocityKickGradual}
                            onChange={setVelocityKickGradual}
                            label="Gradual velocity kicks"
                            title="Spread each kick's velocity change evenly over several frames instead of applying it in one step."
                        />
                    </div>
                    {velocityKickGradual && (
                        <div className="field-row">
                            <label className="field-label">
                                Kick blend (frames)
                                <DebouncedInput
                                    type="number"
                                    min={1}
                                    max={120}
                                    step={1}
                                    value={velocityKickBlendFrames}
                                    onChange={handleInt(
                                        setVelocityKickBlendFrames,
                                        1,
                                        120,
                                    )}
                                />
                            </label>
                        </div>
                    )}
                    <div className="field-row">
                        <label className="field-label">
                            Initial speed
                            <DebouncedInput
                                type="number"
                                min={1}
                                max={2000}
                                step={5}
                                value={baseSpeed}
                                onChange={handleFloat(setBaseSpeed, 1, 2000)}
                            />
                        </label>
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Max speed
                            <DebouncedInput
                                type="number"
                                min={1}
                                max={5000}
                                step={10}
                                value={maxSpeed}
                                onChange={handleFloat(setMaxSpeed, 1, 5000)}
                            />
                        </label>
                    </div>
                    <div className="field-row">
                        <label
                            className="field-label"
                            title="Constrain agent motion to fixed directions. 90° = up/down/left/right; 45° also allows the four diagonals. Speed is preserved."
                        >
                            Direction lock
                            <select
                                value={directionLock}
                                onChange={(e) =>
                                    setDirectionLock(
                                        e.target.value as DirectionLockMode,
                                    )
                                }
                                aria-label="Direction lock"
                            >
                                <option value="none">Free (any angle)</option>
                                <option value="deg90">
                                    90° (up / down / left / right)
                                </option>
                                <option value="deg45">
                                    45° (8 directions)
                                </option>
                            </select>
                        </label>
                    </div>
                    <div className="field-row">
                        <label
                            className="field-label"
                            title="Applies to stroke brush only; sampled per agent at spawn."
                        >
                            Max trail length (px)
                            <DebouncedInput
                                type="number"
                                min={50}
                                max={50000}
                                step={50}
                                value={maxTrailLength}
                                onChange={handleInt(
                                    setMaxTrailLength,
                                    50,
                                    50000,
                                )}
                                debounceMs={400}
                            />
                        </label>
                    </div>
                    <div className="field-row">
                        <label
                            className="field-label"
                            title="Stroke only: 0 = fixed max length; 1 = +/-100% of base per agent."
                        >
                            Trail length randomness (0-1, +/- fraction of max)
                            <DebouncedInput
                                type="number"
                                min={0}
                                max={1}
                                step={0.05}
                                value={maxTrailLengthRandomness}
                                onChange={handleFloat(
                                    setMaxTrailLengthRandomness,
                                    0,
                                    1,
                                )}
                                debounceMs={400}
                            />
                        </label>
                    </div>
                    <div className="field-row">
                        <CheckboxField
                            checked={stopAtMargin}
                            onChange={setStopAtMargin}
                            label="Stop agents at artboard edge"
                            title="When off, trails can continue past the artboard edge until max trail length or other limits."
                        />
                    </div>
                    <div className="field-row">
                        <label
                            className="field-label"
                            title="0 = off. Each agent periodically spawns a new agent at its position (same spawn group)."
                        >
                            Spawn child interval (seconds)
                            <DebouncedInput
                                type="number"
                                min={0}
                                max={60}
                                step={0.05}
                                value={spawnChildIntervalSec}
                                onChange={handleFloat(
                                    setSpawnChildIntervalSec,
                                    0,
                                    60,
                                )}
                                debounceMs={400}
                            />
                        </label>
                    </div>
                    <div className="field-row">
                        <label
                            className="field-label"
                            title="+/- seconds jitter around the spawn interval (next delay is resampled)."
                        >
                            Spawn interval randomness (+/- seconds)
                            <DebouncedInput
                                type="number"
                                min={0}
                                max={30}
                                step={0.05}
                                value={spawnChildIntervalRandomnessSec}
                                onChange={handleFloat(
                                    setSpawnChildIntervalRandomnessSec,
                                    0,
                                    30,
                                )}
                                debounceMs={400}
                            />
                        </label>
                    </div>
                    <div className="field-row">
                        <label
                            className="field-label"
                            title="How to set max trail length for spawned agents (stroke brush). None uses the global max trail length and its randomness."
                        >
                            Child trail cap
                            <select
                                value={childTrailCapMode}
                                onChange={(e) =>
                                    setChildTrailCapMode(
                                        e.target.value as ChildTrailCapMode,
                                    )
                                }
                                disabled={spawnChildIntervalSec <= 0}
                                aria-label="Child trail cap mode"
                            >
                                <option value="none">
                                    None (global max +/- randomness)
                                </option>
                                <option value="parentRemaining">
                                    Parent&apos;s remaining length
                                </option>
                                <option value="fixed">Fixed cap (number)</option>
                            </select>
                        </label>
                    </div>
                    {childTrailCapMode === "fixed" && (
                        <div className="field-row">
                            <label
                                className="field-label"
                                title="Base cap in SVG units before child trail cap randomness is applied."
                            >
                                Child trail cap (px)
                                <DebouncedInput
                                    type="number"
                                    min={50}
                                    max={50000}
                                    step={50}
                                    value={childTrailCapFixed}
                                    onChange={handleInt(
                                        setChildTrailCapFixed,
                                        50,
                                        50000,
                                    )}
                                    debounceMs={400}
                                    disabled={spawnChildIntervalSec <= 0}
                                />
                            </label>
                        </div>
                    )}
                    <div className="field-row">
                        <label
                            className="field-label"
                            title="0 = fixed sampled cap; 1 = +/-100% jitter around the base cap for this mode."
                        >
                            Child trail cap randomness (0-1)
                            <DebouncedInput
                                type="number"
                                min={0}
                                max={1}
                                step={0.05}
                                value={childTrailCapRandomness}
                                onChange={handleFloat(
                                    setChildTrailCapRandomness,
                                    0,
                                    1,
                                )}
                                debounceMs={400}
                                disabled={spawnChildIntervalSec <= 0}
                            />
                        </label>
                    </div>
                    <div className="field-row">
                        <label
                            className="field-label"
                            title="SVG +y is down: positive pulls toward bottom, negative toward top."
                        >
                            Gravity (vertical bias)
                            <DebouncedInput
                                type="number"
                                min={-2000}
                                max={2000}
                                step={5}
                                value={gravity}
                                onChange={handleFloat(setGravity, -2000, 2000)}
                            />
                        </label>
                    </div>
                </CollapsibleToolGroup>

                <CollapsibleToolGroup title="Stroke width" icon={Ruler}>
                    <div className="field-row">
                        <label className="field-label">
                            Min stroke width
                            <DebouncedInput
                                type="number"
                                min={0.05}
                                max={40}
                                step={0.05}
                                value={minStrokeWidth}
                                onChange={handleFloat(
                                    setMinStrokeWidth,
                                    0.05,
                                    40,
                                )}
                            />
                        </label>
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Initial stroke width
                            <DebouncedInput
                                type="number"
                                min={0.25}
                                max={40}
                                step={0.25}
                                value={initialStrokeWidth}
                                onChange={handleFloat(
                                    setInitialStrokeWidth,
                                    minStrokeWidth,
                                    strokeWidth,
                                )}
                            />
                        </label>
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Max stroke width
                            <DebouncedInput
                                type="number"
                                min={0.25}
                                max={40}
                                step={0.25}
                                value={strokeWidth}
                                onChange={handleFloat(
                                    setStrokeWidth,
                                    minStrokeWidth,
                                    40,
                                )}
                            />
                        </label>
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Stroke width step (+/- per kick)
                            <DebouncedInput
                                type="number"
                                min={0}
                                max={20}
                                step={0.05}
                                value={strokeWidthStep}
                                onChange={handleFloat(
                                    setStrokeWidthStep,
                                    0,
                                    20,
                                )}
                            />
                        </label>
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Stroke width kick (frames)
                            <DebouncedInput
                                type="number"
                                min={0}
                                max={600}
                                step={1}
                                value={strokeWidthKickFrames}
                                onChange={handleInt(
                                    setStrokeWidthKickFrames,
                                    0,
                                    600,
                                )}
                            />
                        </label>
                    </div>
                    <div className="field-row">
                        <CheckboxField
                            checked={strokeWidthKickGradual}
                            onChange={setStrokeWidthKickGradual}
                            label="Gradual stroke width kicks"
                            title="Spread each width change evenly over several frames."
                        />
                    </div>
                    {strokeWidthKickGradual && (
                        <div className="field-row">
                            <label className="field-label">
                                Width kick blend (frames)
                                <DebouncedInput
                                    type="number"
                                    min={1}
                                    max={120}
                                    step={1}
                                    value={strokeWidthBlendFrames}
                                    onChange={handleInt(
                                        setStrokeWidthBlendFrames,
                                        1,
                                        120,
                                    )}
                                />
                            </label>
                        </div>
                    )}
                    <p
                        style={{
                            fontSize: "0.85em",
                            opacity: 0.75,
                            margin: "0",
                        }}
                    >
                        Live width is clamped between min and max; kicks use +/-
                        step. Set kick interval to 0 to disable width kicks.
                    </p>
                </CollapsibleToolGroup>

                <CollapsibleToolGroup title="Appearance" icon={Palette}>
                    <div className="field-row">
                        <label className="field-label">
                            Stroke color
                            <input
                                type="color"
                                value={strokeColor}
                                onChange={(e) =>
                                    handleStrokePicker(e.target.value)
                                }
                                aria-label="Stroke color"
                            />
                        </label>
                    </div>
                    <div className="field-row">
                        <label
                            className="field-label"
                            title="Extra colors for random stroke mode. When random off, only the main stroke color is used."
                        >
                            Stroke palette
                            <ColorPaletteEditor
                                colors={parsePaletteTextToColors(
                                    strokePaletteText,
                                )}
                                onChange={(c) =>
                                    setStrokePaletteText(colorsToPaletteText(c))
                                }
                                defaultNewColor={strokeColor}
                                description="Add colors below; each new agent can pick randomly from this list when enabled."
                            />
                        </label>
                    </div>
                    <div className="field-row">
                        <CheckboxField
                            checked={randomStrokeColorPerAgent}
                            onChange={setRandomStrokeColorPerAgent}
                            label="Random stroke color per agent (from palette)"
                            title="Each new agent picks a random color from the palette (or from the main stroke color if the palette is empty). Turn off to use only the main stroke color."
                        />
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Background
                            <input
                                type="color"
                                value={backgroundColor}
                                onChange={(e) => handleBgPicker(e.target.value)}
                                aria-label="Background color"
                            />
                        </label>
                    </div>
                    <div className="field-row">
                        <CheckboxField
                            checked={exportBackground}
                            onChange={setExportBackground}
                            label="Include background in export"
                            inline
                        />
                    </div>
                </CollapsibleToolGroup>

                <CollapsibleToolGroup title="Send to tool" icon={Share2}>
                    <SendSvgToToolMenu
                        disabled={!canExport}
                        getSvg={() => ({
                            name: "path-agents-export.svg",
                            content: buildExportSvgString(),
                        })}
                        allowedSlots={["path", "object", "primary"]}
                        excludeTargetPanels={["path-agents"]}
                    />
                </CollapsibleToolGroup>
            </div>
        </div>
    );
}
